import { clearPlatformSession, ensurePlatformSessionReady } from '@auth/platformSession';
import UrlUtilities from '@shared/common/UrlUtilities';
import SystemActions from '@shared/actions/SystemActions';

declare var InovxLoader: { t(key: string, options?: object): string };

type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

type FormPrimitive = string | number | boolean | Date | null | undefined;
export type FormUrlEncodedValue = FormPrimitive | FormUrlEncodedValue[] | { [key: string]: FormUrlEncodedValue };
export type FormUrlEncodedBody = object;
type FormUrlEncodedRequestBody = FormUrlEncodedBody | FormData | URLSearchParams;

export type ApiAuthMode = 'platform' | 'none';
export type ApiResponseType = 'auto' | 'json' | 'text' | 'blob' | 'arrayBuffer' | 'none';

type ApiBodyOptions =
    | { bodyType?: 'none'; body?: undefined }
    | { bodyType: 'formUrlEncoded'; body: FormUrlEncodedRequestBody }
    | { bodyType: 'formData'; body: FormData }
    | { bodyType: 'json'; body: JsonValue }
    | { bodyType: 'text'; body: string }
    | { bodyType: 'binary'; body: BodyInit };

export type ApiRequestOptions = ApiBodyOptions & {
    url: string;
    method?: string;
    headers?: HeadersInit;
    query?: FormUrlEncodedBody | null;
    authMode?: ApiAuthMode;
    responseType?: ApiResponseType;
    signal?: AbortSignal;
    cache?: RequestCache;
    credentials?: RequestCredentials;
    retryOnAuthFailure?: boolean;
};

interface LegacyErrorPayload {
    Message?: unknown;
    DiagnosticId?: string | number;
    Reason?: string;
    PropertyName?: string;
    title?: unknown;
    detail?: unknown;
    status?: number;
    traceId?: string;
    errors?: Record<string, string[]>;
}

interface StructuredApiMessage {
    Name?: string;
    Value?: string;
    ResourceNotFound?: boolean;
    SearchedLocation?: string;
}

interface ApiErrorInit {
    status: number;
    statusText: string;
    responseText: string;
    responseData: LegacyErrorPayload | null;
    isAbort?: boolean;
}

export class ApiError extends Error {
    readonly status: number;
    readonly statusText: string;
    readonly responseText: string;
    readonly responseData: LegacyErrorPayload | null;
    readonly diagnosticId?: string | number;
    readonly reason?: string;
    readonly propertyName?: string;
    readonly details?: string;
    readonly validationErrors?: Record<string, string[]>;
    readonly isAbort: boolean;

    constructor(message: string, init: ApiErrorInit) {
        super(message);
        this.name = 'ApiError';
        this.status = init.status;
        this.statusText = init.statusText;
        this.responseText = init.responseText;
        this.responseData = init.responseData;
        this.diagnosticId = init.responseData?.DiagnosticId ?? init.responseData?.traceId;
        this.reason = init.responseData?.Reason;
        this.propertyName = init.responseData?.PropertyName;
        this.details = getNormalizedApiMessageContent(init.responseData?.detail) || undefined;
        this.validationErrors = init.responseData?.errors;
        this.isAbort = init.isAbort === true;
    }
}

export async function apiRequest<T>(options: ApiRequestOptions): Promise<T> {
    return executeRequest<T>(options, false);
}

export function apiGet<T>(url: string, query?: FormUrlEncodedBody | null, options?: Omit<ApiRequestOptions, 'url' | 'method' | 'query' | 'bodyType' | 'body'>): Promise<T> {
    return apiRequest<T>({
        ...(options || {}),
        url,
        method: 'GET',
        query,
        bodyType: 'none',
    });
}

export function apiPostFormUrlEncoded<T>(url: string, body: FormUrlEncodedRequestBody, options?: Omit<ApiRequestOptions, 'url' | 'method' | 'bodyType' | 'body'>): Promise<T> {
    return apiRequest<T>({
        ...(options || {}),
        url,
        method: 'POST',
        bodyType: 'formUrlEncoded',
        body,
    });
}

export function apiPostForm<T>(url: string, body: FormData, options?: Omit<ApiRequestOptions, 'url' | 'method' | 'bodyType' | 'body'>): Promise<T> {
    return apiRequest<T>({
        ...(options || {}),
        url,
        method: 'POST',
        bodyType: 'formData',
        body,
    });
}

export function apiPostJson<T>(url: string, body: JsonValue, options?: Omit<ApiRequestOptions, 'url' | 'method' | 'bodyType' | 'body'>): Promise<T> {
    return apiRequest<T>({
        ...(options || {}),
        url,
        method: 'POST',
        bodyType: 'json',
        body,
    });
}

export function toLegacyAjaxError(error: Error | ApiError | unknown): IServiceErrorObject {
    const apiError = toApiError(error);
    const legacyError = {
        readyState: apiError.status > 0 ? 4 : 0,
        responseText: apiError.responseText,
        status: apiError.status,
        statusText: apiError.isAbort ? 'abort' : apiError.statusText,
        messageObj: getApiErrorMessage(apiError),
    };

    return legacyError;
}

export function getApiErrorMessage(error: Error | ApiError | unknown): IErrorMessageObject {
    const apiError = toApiError(error);
    return {
        message: getDisplayMessage(apiError),
        errorId: apiError.diagnosticId?.toString() || '',
        reason: apiError.reason || '',
        propertyName: apiError.propertyName || '',
    };
}

export function displayApiError(error: Error | ApiError | unknown): void {
    const apiError = toApiError(error);
    if (apiError.isAbort || apiError.status === 440) {
        return;
    }

    const messageObj = getApiErrorMessage(error);
    SystemActions.PublicActions.displayErrorMessage({
        message: messageObj.message || getGenericErrorMessage(),
        errorId: messageObj.errorId,
    });
}

async function executeRequest<T>(options: ApiRequestOptions, retryingAfterAuthFailure: boolean): Promise<T> {
    const headers = new Headers(options.headers);
    const body = prepareBody(options, headers);

    if ((options.authMode || 'platform') === 'platform') {
        await attachPlatformAuthorization(headers);
    }

    const response = await fetch(buildUrl(options.url, options.query), {
        method: options.method || (body === undefined ? 'GET' : 'POST'),
        headers,
        body,
        signal: options.signal,
        cache: options.cache,
        credentials: options.credentials,
    }).catch((error: Error) => {
        throw toApiError(error);
    });

    if (!response.ok) {
        if (shouldRetryAfterAuthFailure(response, options, retryingAfterAuthFailure)) {
            clearPlatformSession();
            await ensurePlatformSessionReady();
            return executeRequest<T>(options, true);
        }

        throw await createApiError(response);
    }

    return readResponse<T>(response, options.responseType || 'auto');
}

async function attachPlatformAuthorization(headers: Headers): Promise<void> {
    if (headers.has('Authorization')) {
        return;
    }

    const session = await ensurePlatformSessionReady();
    if (session?.AccessToken) {
        headers.set('Authorization', `Bearer ${session.AccessToken}`);
    }
}

function shouldRetryAfterAuthFailure(response: Response, options: ApiRequestOptions, retryingAfterAuthFailure: boolean): boolean {
    if (retryingAfterAuthFailure || options.retryOnAuthFailure === false || (options.authMode || 'platform') !== 'platform') {
        return false;
    }

    return response.status === 401;
}

function prepareBody(options: ApiRequestOptions, headers: Headers): BodyInit | undefined {
    switch (options.bodyType) {
        case 'formUrlEncoded':
            setHeaderIfMissing(headers, 'Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
            return serializeFormUrlEncoded(options.body);
        case 'formData':
            return options.body;
        case 'json':
            setHeaderIfMissing(headers, 'Content-Type', 'application/json');
            return JSON.stringify(options.body);
        case 'text':
            setHeaderIfMissing(headers, 'Content-Type', 'text/plain; charset=UTF-8');
            return options.body;
        case 'binary':
            return options.body;
        case 'none':
        case undefined:
            return undefined;
    }
}

function buildUrl(url: string, query?: FormUrlEncodedBody | null): string {
    const resolvedUrl = resolveApiUrl(url);
    if (!query || Object.keys(query).length === 0) {
        return resolvedUrl;
    }

    const separator = resolvedUrl.includes('?') ? '&' : '?';
    return `${resolvedUrl}${separator}${serializeFormUrlEncoded(query)}`;
}

function resolveApiUrl(url: string): string {
    if (isAbsoluteUrl(url) || url.startsWith('/')) {
        return url;
    }

    const apiRoot = UrlUtilities.getApiUrl() || '';
    const normalizedRoot = apiRoot.replace(/\/$/, '');
    const normalizedPath = `/${url}`;
    return `${normalizedRoot}${normalizedPath}`;
}

function isAbsoluteUrl(url: string): boolean {
    return /^[a-z][a-z\d+\-.]*:/i.test(url);
}

function serializeFormUrlEncoded(data: FormUrlEncodedRequestBody): string {
    if (data instanceof URLSearchParams) {
        return data.toString().replace(/%20/g, '+');
    }

    if (isFormDataBody(data)) {
        return serializeFormData(data);
    }

    const pairs: string[] = [];
    const record = (data || {}) as Record<string, FormUrlEncodedValue>;

    Object.keys(record).forEach((key) => {
        appendFormValue(pairs, key, record[key]);
    });

    return pairs.join('&').replace(/%20/g, '+');
}

function serializeFormData(data: FormData): string {
    const params = new URLSearchParams();

    for (const [key, value] of data.entries()) {
        if (typeof value !== 'string') {
            throw new Error(`Cannot serialize non-text FormData field '${key}' as application/x-www-form-urlencoded.`);
        }

        params.append(key, value);
    }

    return params.toString().replace(/%20/g, '+');
}

function appendFormValue(pairs: string[], key: string, value: FormUrlEncodedValue): void {
    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            const itemKey = isFormRecord(item) ? `${key}[${index}]` : `${key}[]`;
            appendFormValue(pairs, itemKey, item);
        });
        return;
    }

    if (isFormRecord(value)) {
        Object.keys(value).forEach((childKey) => {
            appendFormValue(pairs, `${key}[${childKey}]`, value[childKey]);
        });
        return;
    }

    pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(formatFormPrimitive(value))}`);
}

function isFormRecord(value: FormUrlEncodedValue): value is { [key: string]: FormUrlEncodedValue } {
    return typeof value === 'object' && value !== null && !(value instanceof Date) && !Array.isArray(value);
}

function isFormDataBody(value: FormUrlEncodedRequestBody): value is FormData {
    return typeof FormData !== 'undefined' && value instanceof FormData;
}

function formatFormPrimitive(value: FormPrimitive): string {
    if (value === null || value === undefined) {
        return '';
    }

    return value instanceof Date ? value.toString() : String(value);
}

function setHeaderIfMissing(headers: Headers, key: string, value: string): void {
    if (!headers.has(key)) {
        headers.set(key, value);
    }
}

async function readResponse<T>(response: Response, responseType: ApiResponseType): Promise<T> {
    if (response.status === 204 || responseType === 'none') {
        return undefined as T;
    }

    if (responseType === 'blob') {
        return await response.blob() as T;
    }

    if (responseType === 'arrayBuffer') {
        return await response.arrayBuffer() as T;
    }

    if (responseType === 'text') {
        return await response.text() as T;
    }

    if (responseType === 'json') {
        return await response.json() as T;
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.toLowerCase().includes('application/json')) {
        return await response.json() as T;
    }

    return await response.text() as T;
}

async function createApiError(response: Response): Promise<ApiError> {
    const responseText = await response.text().catch(() => '');
    const responseData = parseErrorPayload(responseText);
    const message: string = getNormalizedApiMessageContent(responseData?.Message)
        || getNormalizedApiMessageContent(responseData?.title)
        || getNormalizedApiMessageContent(responseData?.detail)
        || response.statusText
        || `Request failed with status: ${response.status}`;

    return new ApiError(message, {
        status: response.status,
        statusText: response.statusText,
        responseText,
        responseData,
    });
}

function toApiError(error: Error | ApiError | unknown): ApiError {
    if (error instanceof ApiError) {
        return error;
    }

    if (isAbortError(error)) {
        return new ApiError('Request aborted.', {
            status: 0,
            statusText: 'abort',
            responseText: '',
            responseData: null,
            isAbort: true,
        });
    }

    if (isLegacyAjaxError(error)) {
        const responseData = parseErrorPayload(error.responseText);
        const message = getNormalizedApiMessageContent(responseData?.Message)
            || getNormalizedApiMessageContent(responseData?.title)
            || getNormalizedApiMessageContent(responseData?.detail)
            || error.statusText
            || getGenericErrorMessage();
        return new ApiError(message, {
            status: error.status || 0,
            statusText: error.statusText || '',
            responseText: error.responseText || '',
            responseData,
            isAbort: error.statusText === 'abort',
        });
    }

    if (error instanceof Error) {
        return new ApiError(error.message || getGenericErrorMessage(), {
            status: 0,
            statusText: error.name || '',
            responseText: '',
            responseData: null,
        });
    }

    return new ApiError(getGenericErrorMessage(), {
        status: 0,
        statusText: '',
        responseText: '',
        responseData: null,
    });
}

function parseErrorPayload(responseText: string): LegacyErrorPayload | null {
    if (!responseText) {
        return null;
    }

    try {
        const parsed = JSON.parse(responseText) as LegacyErrorPayload;
        return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
        return null;
    }
}

function getDisplayMessage(apiError: ApiError): string {
    return getNormalizedApiMessageContent(apiError.responseData?.Message)
        || getNormalizedApiMessageContent(apiError.responseData?.title)
        || getNormalizedApiMessageContent(apiError.responseData?.detail)
        || apiError.message
        || getGenericErrorMessage();
}

function getNormalizedApiMessageContent(value: unknown): string | null {
    if (typeof value === 'string') {
        return value;
    }

    if (isStructuredApiMessage(value)) {
        return value.Value || value.Name || null;
    }

    return null;
}

function isStructuredApiMessage(value: unknown): value is StructuredApiMessage {
    return typeof value === 'object'
        && value !== null
        && (
            typeof (value as StructuredApiMessage).Value === 'string'
            || typeof (value as StructuredApiMessage).Name === 'string'
        );
}

function isAbortError(error: Error | ApiError | unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
}

function isLegacyAjaxError(error: Error | ApiError | unknown): error is IAjaxErrorObject {
    return typeof error === 'object' && error !== null && 'responseText' in error && 'status' in error && 'statusText' in error;
}

function getGenericErrorMessage(): string {
    return typeof InovxLoader !== 'undefined' ? InovxLoader.t('common:error.generic') : 'An unexpected error occurred.';
}
